import { MonitoringCategory } from "@domain/product/monitoring/monitoring-product.service";
import { Project } from "@domain/project/base-project";
import { ChecklistMonitoring } from "@domain/project/checklist-monitoring";
import { CentralStationConfiguration } from "@domain/project/configurations/monitoring/central-station-configuration";
import { DisplayConfiguration } from "@domain/project/configurations/monitoring/display-configuration";
import { KvmConfiguration } from "@domain/project/configurations/monitoring/kvm-configuration";
import { PatientMonitorConfiguration } from "@domain/project/configurations/monitoring/patient-monitor-configuration";
import { TelemetryConfiguration } from "@domain/project/configurations/monitoring/telemetry-configuration";
import { UtilityConfiguration } from "@domain/project/configurations/monitoring/utility-configuration";
import { ProductConfiguration } from "@domain/project/configurations/product-configuration";
import { MonitoringBomService } from "@domain/project/monitoring-bom-service";
import { ProjectEventType } from "@domain/project/project-event";
import { BusinessScope } from "@lib/constants/business-scope";
import { IsArrayOfInstancesOf, IsInstanceOf } from "@utils/class-validator/class-validator-constraints";
import { Expose, Transform, Type } from "class-transformer";
import { IsOptional, IsString, ValidateNested } from "class-validator";
import { v4 as uuidv4 } from "uuid";
import { MonitoringDirectBomEntry, MonitoringDirectBomLine } from "./direct-bom/monitoring-direct-bom-entry";

export class MonitoringProject extends Project {
  static businessScope: BusinessScope = BusinessScope.MONITORING;

  @IsString()
  @IsOptional()
  public draegerContactPerson?: string;
  @ValidateNested()
  @IsArrayOfInstancesOf(PatientMonitorConfiguration)
  @Type(() => PatientMonitorConfiguration)
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  @Expose({ name: "patientMonitors" })
  private readonly _patientMonitors: PatientMonitorConfiguration[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(CentralStationConfiguration)
  @Type(() => CentralStationConfiguration)
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  @Expose({ name: "centralStations" })
  private readonly _centralStations: CentralStationConfiguration[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(TelemetryConfiguration)
  @Type(() => TelemetryConfiguration)
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  @Expose({ name: "telemetries" })
  private readonly _telemetries: TelemetryConfiguration[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(KvmConfiguration)
  @Type(() => KvmConfiguration)
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  @Expose({ name: "kvms" })
  private readonly _kvms: KvmConfiguration[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(DisplayConfiguration)
  @Type(() => DisplayConfiguration)
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  @Expose({ name: "displays" })
  private readonly _displays: DisplayConfiguration[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(UtilityConfiguration)
  @Type(() => UtilityConfiguration)
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  @Expose({ name: "utilities" })
  private readonly _utilities: UtilityConfiguration[] = [];

  @IsArrayOfInstancesOf(MonitoringDirectBomEntry)
  @ValidateNested({ each: true })
  @Type(() => MonitoringDirectBomEntry)
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  @Expose({ name: "directBomEntries" })
  private readonly _directBomEntries: MonitoringDirectBomEntry[] = [];

  @IsInstanceOf(ChecklistMonitoring)
  @ValidateNested()
  @Type(() => ChecklistMonitoring)
  @Expose({ name: "checklistMonitoring" })
  @Transform((params) => params.value || new ChecklistMonitoring(), {
    toClassOnly: true,
  })
  private readonly _checklistMonitoring: ChecklistMonitoring = new ChecklistMonitoring();

  @IsArrayOfInstancesOf(MonitoringBomService)
  @IsOptional()
  @ValidateNested({ each: true })
  @Type(() => MonitoringBomService)
  @Expose({ name: "monitoringBomServices" })
  @Transform((params) => MonitoringBomService.createAll(params.value), {
    toClassOnly: true,
  })
  private readonly _monitoringBomServices: MonitoringBomService[] = MonitoringBomService.createDefaults();

  override get businessScope(): BusinessScope {
    return (this.constructor as any).businessScope ?? BusinessScope.MONITORING;
  }

  get patientMonitors(): readonly PatientMonitorConfiguration[] {
    this._patientMonitors.forEach((patientMonitor) => patientMonitor.init(this));
    return this._patientMonitors;
  }

  get centralStations(): readonly CentralStationConfiguration[] {
    this._centralStations.forEach((centralStation) => centralStation.init(this));
    return this._centralStations;
  }

  get telemetries(): readonly TelemetryConfiguration[] {
    this._telemetries.forEach((telemetry) => telemetry.init(this));
    return this._telemetries;
  }

  get kvms(): readonly KvmConfiguration[] {
    this._kvms.forEach((kvm) => kvm.init(this));
    return this._kvms;
  }

  get displays(): readonly DisplayConfiguration[] {
    this._displays.forEach((display) => display.init(this));
    return this._displays;
  }

  get utilities(): readonly UtilityConfiguration[] {
    this._utilities.forEach((utility) => utility.init(this));
    return this._utilities;
  }

  get directBomEntries(): readonly MonitoringDirectBomEntry[] {
    return this._directBomEntries;
  }

  get checklistMonitoring(): ChecklistMonitoring {
    return this._checklistMonitoring;
  }

  get monitoringBomServices(): MonitoringBomService[] {
    return this._monitoringBomServices;
  }

  get productConfigurations(): ProductConfiguration[] {
    return new Array<ProductConfiguration>().concat(
      this._patientMonitors,
      this._centralStations,
      this._telemetries,
      this._kvms,
      this._displays,
      this._utilities,
    );
  }

  addPatientMonitors(...patientMonitors: PatientMonitorConfiguration[]) {
    this._patientMonitors.push(...patientMonitors);
    patientMonitors.forEach((config) => this.publishUpdate(ProjectEventType.PATIENT_MONITOR_CONFIGURATION_ADDED, config));
  }

  getPatientMonitorById(patientMonitorId: string): PatientMonitorConfiguration | undefined {
    return this._patientMonitors.find((patientMonitor) => patientMonitor.id === patientMonitorId);
  }

  deletePatientMonitor(patientMonitor: PatientMonitorConfiguration) {
    this.deleteConfiguration(
      patientMonitor,
      this._patientMonitors,
      ProjectEventType.PATIENT_MONITOR_CONFIGURATION_DELETED,
      (floorplan, config) => floorplan.deletePatientMonitorsByConfig(config),
    );
  }

  addCentralStations(...centralStations: CentralStationConfiguration[]) {
    this._centralStations.push(...centralStations);
    centralStations.forEach((config) => this.publishUpdate(ProjectEventType.CENTRAL_STATION_CONFIGURATION_ADDED, config));
  }

  getCentralStationById(centralStationId: string): CentralStationConfiguration | undefined {
    return this._centralStations.find((centralStation) => centralStation.id === centralStationId);
  }

  deleteCentralStation(centralStation: CentralStationConfiguration) {
    this.deleteConfiguration(
      centralStation,
      this._centralStations,
      ProjectEventType.CENTRAL_STATION_CONFIGURATION_DELETED,
      (floorplan, config) => floorplan.deleteCentralStationsByConfig(config),
    );
  }

  addTelemetries(...telemetries: TelemetryConfiguration[]) {
    this._telemetries.push(...telemetries);
    telemetries.forEach((config) => this.publishUpdate(ProjectEventType.TELEMETRY_CONFIGURATION_ADDED, config));
  }

  getTelemetryById(telemetryId: string): TelemetryConfiguration | undefined {
    return this._telemetries.find((telemetry) => telemetry.id === telemetryId);
  }

  deleteTelemetry(telemetry: TelemetryConfiguration) {
    this.deleteConfiguration(
      telemetry,
      this._telemetries,
      ProjectEventType.TELEMETRY_CONFIGURATION_DELETED,
      (floorplan, config) => floorplan.deleteTelemetriesByConfig(config),
    );
  }

  addKvms(...kvms: KvmConfiguration[]) {
    this._kvms.push(...kvms);
    kvms.forEach((config) => this.publishUpdate(ProjectEventType.KVM_CONFIGURATION_ADDED, config));
  }

  getKvmById(kvmId: string): KvmConfiguration | undefined {
    return this._kvms.find((kvm) => kvm.id === kvmId);
  }

  deleteKvm(kvm: KvmConfiguration) {
    this.deleteConfiguration(kvm, this._kvms, ProjectEventType.KVM_CONFIGURATION_DELETED, (floorplan, config) =>
      floorplan.deleteKvmsByConfig(config),
    );
  }

  addDisplays(...displays: DisplayConfiguration[]) {
    this._displays.push(...displays);
    displays.forEach((config) => this.publishUpdate(ProjectEventType.DISPLAY_CONFIGURATION_ADDED, config));
  }

  getDisplayById(displayId: string): DisplayConfiguration | undefined {
    return this._displays.find((display) => display.id === displayId);
  }

  deleteDisplay(display: DisplayConfiguration) {
    this.deleteConfiguration(display, this._displays, ProjectEventType.DISPLAY_CONFIGURATION_DELETED, (floorplan, config) =>
      floorplan.deleteDisplaysByConfig(config),
    );
  }

  addUtilities(...utilities: UtilityConfiguration[]) {
    this._utilities.push(...utilities);
    utilities.forEach((config) => this.publishUpdate(ProjectEventType.UTILITY_CONFIGURATION_ADDED, config));
  }

  getUtilityById(utilityId: string): UtilityConfiguration | undefined {
    return this._utilities.find((utility) => utility.id === utilityId);
  }

  deleteUtility(utility: UtilityConfiguration) {
    this.deleteConfiguration(utility, this._utilities, ProjectEventType.UTILITY_CONFIGURATION_DELETED, (floorplan, config) =>
      floorplan.deleteUtilitiesByConfig(config),
    );
  }

  override setPositionNumbers() {
    this._patientMonitors.forEach((c) => c.init(this));
    this._centralStations.forEach((c) => c.init(this));
    this._telemetries.forEach((c) => c.init(this));
    this._kvms.forEach((c) => c.init(this));
    this._displays.forEach((c) => c.init(this));
    this._utilities.forEach((c) => c.init(this));

    let startingPositionNumber = 1;

    this.setPositionNumber(startingPositionNumber, this._patientMonitors, (floorplan, config) => {
      floorplan.updatePatientMonitorsPositionIds(config);
    });
    startingPositionNumber += this.countPlacedConfigurations(this._patientMonitors);

    this.setPatientMonitorPlaceholderPositionIds(startingPositionNumber);
    startingPositionNumber += this.countPlaceholdersWithProductId((floorplan) => floorplan.patientMonitorPlaceholders);

    this.setPositionNumber(startingPositionNumber, this._centralStations, (floorplan, config) => {
      floorplan.updateCentralStationsPositionIds(config);
    });
    startingPositionNumber += this.countPlacedConfigurations(this._centralStations);

    this.setCentralStationPlaceholderPositionIds(startingPositionNumber);
    startingPositionNumber += this.countPlaceholdersWithProductId((floorplan) => floorplan.centralStationPlaceholders);

    this.setPositionNumber(startingPositionNumber, this._telemetries, (floorplan, config) => {
      floorplan.updateTelemetriesPositionIds(config);
    });
    startingPositionNumber += this.countPlacedConfigurations(this._telemetries);

    this.setTelemetryPlaceholderPositionIds(startingPositionNumber);
    startingPositionNumber += this.countPlaceholdersWithProductId((floorplan) => floorplan.telemetryPlaceholders);

    this.setPositionNumber(startingPositionNumber, this._kvms, (floorplan, config) => {
      floorplan.updateKvmsPositionIds(config);
    });
    startingPositionNumber += this.countPlacedConfigurations(this._kvms);

    this.setKvmPlaceholderPositionIds(startingPositionNumber);
    startingPositionNumber += this.countPlaceholdersWithProductId((floorplan) => floorplan.kvmPlaceholders);

    this.setPositionNumber(startingPositionNumber, this._displays, (floorplan, config) => {
      floorplan.updateDisplaysPositionIds(config);
    });
    startingPositionNumber += this.countPlacedConfigurations(this._displays);

    this.setDisplayPlaceholderPositionIds(startingPositionNumber);
    startingPositionNumber += this.countPlaceholdersWithProductId((floorplan) => floorplan.displayPlaceholders);

    this.setPositionNumber(startingPositionNumber, this._utilities, (floorplan, config) => {
      floorplan.updateUtilitiesPositionIds(config);
    });
    startingPositionNumber += this.countPlacedConfigurations(this._utilities);

    this.setUtilityPlaceholderPositionIds(startingPositionNumber);
    startingPositionNumber += this.countPlaceholdersWithProductId((floorplan) => floorplan.utilityPlaceholders);

    this.setAdditionalEntryPositionNumbers(startingPositionNumber, this._directBomEntries);
  }

  addProductConfig(config: ProductConfiguration, category: MonitoringCategory): void {
    switch (category) {
      case MonitoringCategory.PATIENT_MONITORS:
        this.addPatientMonitors(config as PatientMonitorConfiguration);
        break;
      case MonitoringCategory.CENTRAL_STATIONS:
        this.addCentralStations(config as CentralStationConfiguration);
        break;
      case MonitoringCategory.TELEMETRY:
        this.addTelemetries(config as TelemetryConfiguration);
        break;
      case MonitoringCategory.KVMS:
        this.addKvms(config as KvmConfiguration);
        break;
      case MonitoringCategory.DISPLAYS:
        this.addDisplays(config as DisplayConfiguration);
        break;
      case MonitoringCategory.UTILITY:
        this.addUtilities(config as UtilityConfiguration);
        break;
    }
  }

  findProductConfigByDevice(deviceMatNr: string): ProductConfiguration | undefined {
    return this.productConfigurations.find((config) => config.configuredProductIds.includes(deviceMatNr));
  }

  findProductConfigByGroup(groupName: string, groupDevices?: string[]): ProductConfiguration | undefined {
    return this.productConfigurations.find((config) => {
      const nameMatches = config.name === groupName || config.name.includes(groupName);
      if (!nameMatches) return false;

      if (!groupDevices) return true;

      return (
        config.configuredProductIds.length === groupDevices.length &&
        groupDevices.every((d) => config.configuredProductIds.includes(d))
      );
    });
  }

  addDirectBomEntry(entry: MonitoringDirectBomEntry): void {
    this._directBomEntries.push(entry);
    this.setPositionNumbers();
    this.publishUpdate(ProjectEventType.DIRECT_BOM_ENTRY_ADDED, entry);
  }

  setDirectBomEntryQuantity(entry: MonitoringDirectBomEntry, quantity: number): void {
    const nextQuantity = Number.isFinite(quantity) && quantity > 0 ? Math.floor(quantity) : 0;
    if (entry.quantity === nextQuantity) {
      return;
    }

    entry.quantity = nextQuantity;
    this.setPositionNumbers();
    this.publishUpdate(ProjectEventType.DIRECT_BOM_ENTRY_UPDATED, entry);
  }

  findDirectBomEntryByLines(
    category: MonitoringCategory,
    lines: MonitoringDirectBomLine[],
  ): MonitoringDirectBomEntry | undefined {
    const lineKey = this.serializeDirectBomLines(lines);
    return this._directBomEntries.find(
      (entry) => entry.category === category && this.serializeDirectBomLines(entry.lines) === lineKey,
    );
  }

  private serializeDirectBomLines(lines: MonitoringDirectBomLine[]) {
    return [...lines]
      .sort((a, b) => a.partNumber.localeCompare(b.partNumber))
      .map((line) => `${line.partNumber}:${line.quantity}`)
      .join("|");
  }

  static create(name: string, status: string, country: string): MonitoringProject {
    return new MonitoringProject(uuidv4(), name, status, country);
  }

  override validate(): void {}
}
